import React from 'react';
import { Shield, Lock, Eye, Database, Share2, Cookie, Bell, UserCheck, Mail, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF4D00] to-[#E04400] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold" data-testid="privacy-policy-title">Privacy Policy</h1>
          </div>
          <p className="text-white/80">Your privacy is important to us. Learn how we collect, use, and protect your data.</p>
          <p className="text-white/60 text-sm mt-2">Last Updated: January 2026</p>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#FF4D00]" />
              Introduction
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Welcome to RSD Accessories Pvt. Ltd. ("RSD", "we", "us", or "our"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>rsdaccessories.com</strong> or make a purchase from us.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site. We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-neutral-700">
                <strong>Company Information:</strong><br />
                RSD Accessories Pvt. Ltd. (A Brand of New Eon)<br />
                B-08, Sector-57, Noida-201301, Uttar Pradesh, India<br />
                Email: contact@rsdaccessories.com<br />
                Phone: +91 8595459161
              </p>
            </div>
          </section>
          
          {/* Information We Collect */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#FF4D00]" />
              Information We Collect
            </h2>
            
            <h3 className="font-semibold text-neutral-800 mt-4 mb-2">Personal Information You Provide</h3>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We collect personal information that you voluntarily provide to us when you register on the website, make a purchase, subscribe to our newsletter, or contact us. This includes:
            </p>
            <ul className="list-disc pl-6 text-neutral-600 space-y-1 mb-4">
              <li><strong>Identity Data:</strong> Name, username, date of birth, gender</li>
              <li><strong>Contact Data:</strong> Email address, phone number, billing address, shipping address</li>
              <li><strong>Transaction Data:</strong> Details about payments, products purchased, order history</li>
              <li><strong>Profile Data:</strong> Your preferences, feedback, survey responses</li>
              <li><strong>Communication Data:</strong> Your correspondence with us via email, phone, or chat</li>
            </ul>
            
            <h3 className="font-semibold text-neutral-800 mt-4 mb-2">Information Automatically Collected</h3>
            <p className="text-neutral-600 leading-relaxed mb-3">
              When you visit our website, we automatically collect certain information about your device and usage:
            </p>
            <ul className="list-disc pl-6 text-neutral-600 space-y-1 mb-4">
              <li><strong>Device Data:</strong> IP address, browser type and version, operating system, device type</li>
              <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, search queries</li>
              <li><strong>Location Data:</strong> Country, city (based on IP address)</li>
              <li><strong>Referral Data:</strong> How you arrived at our website (search engine, social media, direct)</li>
            </ul>
            
            <h3 className="font-semibold text-neutral-800 mt-4 mb-2">Payment Information</h3>
            <p className="text-neutral-600 leading-relaxed">
              All payment transactions are processed through <strong>Razorpay</strong>, our secure payment gateway. We do NOT store your credit/debit card numbers, CVV, or banking credentials on our servers. Razorpay handles all payment data in compliance with PCI-DSS (Payment Card Industry Data Security Standard).
            </p>
          </section>
          
          {/* How We Use Your Information */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#FF4D00]" />
              How We Use Your Information
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-3">
              We use the information we collect for various purposes, including:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800 mb-2">Order Processing</h4>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li>• Process and fulfill your orders</li>
                  <li>• Send order confirmations and updates</li>
                  <li>• Handle payments and refunds</li>
                  <li>• Provide shipping and delivery updates</li>
                </ul>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800 mb-2">Customer Service</h4>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li>• Respond to inquiries and support requests</li>
                  <li>• Process warranty claims</li>
                  <li>• Handle returns and replacements</li>
                  <li>• Resolve disputes and complaints</li>
                </ul>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800 mb-2">Marketing (With Consent)</h4>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li>• Send promotional offers and discounts</li>
                  <li>• Notify about new products</li>
                  <li>• Share personalized recommendations</li>
                  <li>• Conduct surveys and feedback requests</li>
                </ul>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800 mb-2">Website Improvement</h4>
                <ul className="text-sm text-neutral-600 space-y-1">
                  <li>• Analyze website traffic and usage</li>
                  <li>• Improve user experience</li>
                  <li>• Fix bugs and technical issues</li>
                  <li>• Develop new features and services</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-neutral-700">
                <strong>Legal Basis for Processing (GDPR Compliance):</strong> We process your data based on: (a) Your consent, (b) Performance of a contract (order fulfillment), (c) Legal obligations, (d) Legitimate business interests.
              </p>
            </div>
          </section>
          
          {/* Cookies and Tracking */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-[#FF4D00]" />
              Cookies and Tracking Technologies
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to collect and store information about your interactions with our website.
            </p>
            
            <h3 className="font-semibold text-neutral-800 mt-4 mb-2">Types of Cookies We Use</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-neutral-100">
                    <th className="border border-neutral-200 px-4 py-2 text-left">Cookie Type</th>
                    <th className="border border-neutral-200 px-4 py-2 text-left">Purpose</th>
                    <th className="border border-neutral-200 px-4 py-2 text-left">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-neutral-600">
                  <tr>
                    <td className="border border-neutral-200 px-4 py-2 font-medium">Essential Cookies</td>
                    <td className="border border-neutral-200 px-4 py-2">Required for website functionality, shopping cart, checkout</td>
                    <td className="border border-neutral-200 px-4 py-2">Session</td>
                  </tr>
                  <tr>
                    <td className="border border-neutral-200 px-4 py-2 font-medium">Analytics Cookies</td>
                    <td className="border border-neutral-200 px-4 py-2">Track website usage, page views, traffic sources</td>
                    <td className="border border-neutral-200 px-4 py-2">2 years</td>
                  </tr>
                  <tr>
                    <td className="border border-neutral-200 px-4 py-2 font-medium">Preference Cookies</td>
                    <td className="border border-neutral-200 px-4 py-2">Remember your settings, language, location</td>
                    <td className="border border-neutral-200 px-4 py-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="border border-neutral-200 px-4 py-2 font-medium">Marketing Cookies</td>
                    <td className="border border-neutral-200 px-4 py-2">Personalized ads, retargeting (with consent)</td>
                    <td className="border border-neutral-200 px-4 py-2">90 days</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <h3 className="font-semibold text-neutral-800 mt-4 mb-2">Managing Cookies</h3>
            <p className="text-neutral-600 leading-relaxed">
              You can control cookies through your browser settings. Most browsers allow you to refuse cookies or delete existing cookies. However, disabling essential cookies may affect website functionality.
            </p>
          </section>
          
          {/* Information Sharing */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#FF4D00]" />
              Information Sharing and Disclosure
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              <strong>We do NOT sell, rent, or trade your personal information to third parties.</strong> We may share your information only in the following circumstances:
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-[#FF4D00] pl-4">
                <h4 className="font-semibold text-neutral-800">Service Providers</h4>
                <p className="text-sm text-neutral-600">We share data with trusted partners who assist in our operations:</p>
                <ul className="text-sm text-neutral-600 mt-1">
                  <li>• <strong>Shipping Partners:</strong> Delhivery, BlueDart, DTDC (name, address, phone for delivery)</li>
                  <li>• <strong>Payment Processor:</strong> Razorpay (payment processing only)</li>
                  <li>• <strong>Email Service:</strong> For order confirmations and communications</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-[#FF4D00] pl-4">
                <h4 className="font-semibold text-neutral-800">Legal Requirements</h4>
                <p className="text-sm text-neutral-600">We may disclose information if required by law, court order, or government request, or to protect our legal rights and safety.</p>
              </div>
              
              <div className="border-l-4 border-[#FF4D00] pl-4">
                <h4 className="font-semibold text-neutral-800">Business Transfers</h4>
                <p className="text-sm text-neutral-600">In case of merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction.</p>
              </div>
              
              <div className="border-l-4 border-[#FF4D00] pl-4">
                <h4 className="font-semibold text-neutral-800">With Your Consent</h4>
                <p className="text-sm text-neutral-600">We may share information for other purposes with your explicit consent.</p>
              </div>
            </div>
          </section>
          
          {/* Data Security */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#FF4D00]" />
              Data Security
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your personal information:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">SSL Encryption</h4>
                  <p className="text-sm text-neutral-600">All data transmitted between your browser and our servers is encrypted using SSL/TLS technology.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Secure Payments</h4>
                  <p className="text-sm text-neutral-600">Razorpay handles payments with PCI-DSS compliance. We never store card details.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Data Access Control</h4>
                  <p className="text-sm text-neutral-600">Limited access to personal data. Only authorized personnel can access customer information.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-800">Regular Audits</h4>
                  <p className="text-sm text-neutral-600">Periodic security assessments and updates to maintain data protection standards.</p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-neutral-700">
                <strong>Important:</strong> While we strive to protect your data, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but are committed to protecting your information to the best of our ability.
              </p>
            </div>
          </section>
          
          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#FF4D00]" />
              Data Retention
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy:
            </p>
            <ul className="list-disc pl-6 text-neutral-600 space-y-2">
              <li><strong>Account Data:</strong> Retained until you request deletion or close your account</li>
              <li><strong>Order Data:</strong> Retained for 7 years for tax, legal, and warranty purposes</li>
              <li><strong>Communication Records:</strong> Retained for 3 years for customer service reference</li>
              <li><strong>Marketing Preferences:</strong> Retained until you unsubscribe or withdraw consent</li>
              <li><strong>Website Analytics:</strong> Aggregated data retained indefinitely; identifiable data for 26 months</li>
            </ul>
          </section>
          
          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#FF4D00]" />
              Your Rights
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              You have certain rights regarding your personal information under applicable data protection laws:
            </p>
            
            <div className="space-y-3">
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Access</h4>
                <p className="text-sm text-neutral-600">Request a copy of the personal information we hold about you.</p>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Correction</h4>
                <p className="text-sm text-neutral-600">Request correction of inaccurate or incomplete personal information.</p>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Deletion</h4>
                <p className="text-sm text-neutral-600">Request deletion of your personal information (subject to legal retention requirements).</p>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Object</h4>
                <p className="text-sm text-neutral-600">Object to processing of your data for marketing purposes.</p>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Data Portability</h4>
                <p className="text-sm text-neutral-600">Request transfer of your data to another service provider.</p>
              </div>
              
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-800">Right to Withdraw Consent</h4>
                <p className="text-sm text-neutral-600">Withdraw consent for marketing communications at any time.</p>
              </div>
            </div>
            
            <p className="text-neutral-600 mt-4">
              To exercise any of these rights, please contact us at <strong>contact@rsdaccessories.com</strong>. We will respond to your request within 30 days.
            </p>
          </section>
          
          {/* Marketing Communications */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#FF4D00]" />
              Marketing Communications
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We may send you marketing communications if you have opted in or made a purchase from us. You can opt out at any time:
            </p>
            <ul className="list-disc pl-6 text-neutral-600 space-y-1">
              <li>Click the "Unsubscribe" link in any marketing email</li>
              <li>Reply "STOP" to SMS marketing messages</li>
              <li>Contact us at contact@rsdaccessories.com with your request</li>
              <li>Update your preferences in your account settings</li>
            </ul>
            <p className="text-neutral-600 mt-4 text-sm">
              <strong>Note:</strong> Even after opting out of marketing, you will still receive transactional emails related to your orders (confirmations, shipping updates, etc.).
            </p>
          </section>
          
          {/* Third-Party Links */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">Third-Party Links</h2>
            <p className="text-neutral-600 leading-relaxed">
              Our website may contain links to third-party websites (e.g., social media platforms, payment gateways). We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.
            </p>
          </section>
          
          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">Children's Privacy</h2>
            <p className="text-neutral-600 leading-relaxed">
              Our website is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If we discover that a child has provided us with personal information, we will delete it immediately. If you believe a child has provided information to us, please contact us.
            </p>
          </section>
          
          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-neutral-600 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of significant changes by posting a notice on our website or sending an email. Your continued use of our website after changes constitutes acceptance of the updated policy.
            </p>
          </section>
          
          {/* Contact Us */}
          <section className="bg-gradient-to-r from-orange-50 to-white rounded-xl p-6 border border-orange-200">
            <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#FF4D00]" />
              Contact Us
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#FF4D00]" />
                <div>
                  <p className="text-sm text-neutral-500">Email</p>
                  <a href="mailto:contact@rsdaccessories.com" className="text-[#FF4D00] font-medium hover:underline">contact@rsdaccessories.com</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#FF4D00]" />
                <div>
                  <p className="text-sm text-neutral-500">Phone / WhatsApp</p>
                  <a href="tel:+918595459161" className="text-[#FF4D00] font-medium hover:underline">+91 8595459161</a>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-orange-200">
              <p className="text-sm text-neutral-600">
                <strong>Grievance Officer:</strong><br />
                RSD Accessories Pvt. Ltd.<br />
                B-08, Sector-57, Noida-201301, U.P., India<br />
                Response Time: Within 48 hours
              </p>
            </div>
          </section>
          
        </div>
        
        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <p>© 2026 RSD Accessories Pvt. Ltd. All Rights Reserved.</p>
          <p className="mt-1">This Privacy Policy is effective from January 2026.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
